import axios from 'axios'
import { appLog, handleServerError, getUserProfile,
  getDebugInfo, handleTradeSessionFailed } from '@/utils/nativeBridge'
import { getSignKey, aesEncrypt, aesDecrypt } from '@/authLib'
import { generateUUID, getProduct, parseUrl } from '@/authLib/tools'
import {
  encrytedContentType,
  terminalKey,
  ENCRYTEDCONTENTTYPE2
} from '@/authLib/config.js'
import config from '@/service/config'
import Utils from '@/utils/utils'
import store from '@/store'
import { clearLoignCookie, isLoginCookieExist } from '@/utils/cookie'
import { sessionCode, tradeSessionCode } from '@/config/common'
const env = config.env

// pay attention to this key
const terminalDev = env === 'Live' || env === 'WebLive' ? 'live' : 'default'

function needXsign (options, url, config) {
  const { key } = terminalKey[terminalDev]
  const allUrl = `${config.baseURL}${url}`
  const objUrl = parseUrl(allUrl)
  const tempReq = {
    ...options.headers,
    urlPath: objUrl.path,
    allUrl,
    method: options.method,
    data: options.method.toUpperCase() === 'GET' ? '' : options.data
  }
  options.headers['x-sign'] = getSignKey(
    tempReq,
    key,
    'h5',
    store.rndkey
  )
}
function isString (arg) {
  return typeof arg === 'string'
}

/**
 * 设置通用请求头配置
 * @param {Object} options 选项
 * @param {String} url 接口地址
 * @param {Object} config axios全局配置
 */
function setComReqHeader (options, url, config) {
  const allUrl = `${config.baseURL}${url}`
  const objUrl = parseUrl(allUrl)
  const { product, version } = terminalKey[terminalDev]
  options.headers['x-product'] = getProduct(product, version).product
  options.headers['x-requestid'] = generateUUID()
  options.headers['x-antispams'] = `0:${generateUUID()}`
  options.headers['x-device'] = navigator.userAgent
  options.headers['x-timestamp'] = +new Date()
  options.headers['x-host'] = objUrl.host
  options.headers['accept-language'] = Utils.getQueryString('lang') || 'zh-HK'
}

/**
 * axiosRequest
 *
 * @param {Object} config about axios
 */
export function axiosRequest (config = {}) {
  var axiosIns = axios.create(config)

  //  请求加密
  axiosIns.interceptors.request.use(req => {
    if (req.headers['content-type'] === encrytedContentType && req.data) {
      req.data = aesEncrypt(
        typeof req.data === 'object' ? JSON.stringify(req.data) : req.data,
        store.rndkey
      )
    }
    // appLog(1, `api options, options=${JSON.stringify(req, null, 2)}`)
    return req
  })

  // 请求头签名
  axiosIns.interceptors.request.use(req => {
    if (req.headers['need-xsign']) {
      needXsign(req, req.url, config)
    }
    delete req.headers['need-xsign']
    return req
  })

  // 处理请求cookie
  axiosIns.interceptors.request.use(req => {
    // 若登录cookie不存在，则获取native登录信息
    if (!isLoginCookieExist() && !Utils.getUrlQuery().isShare) {
      return new Promise(resolve => {
        getUserProfile()
          .then((data) => {
            Utils.setStore(data)
            return resolve(req)
          })
          .catch(err => {
            appLog(3, `get user profile failed, err=${JSON.stringify(err)}`)
            return resolve(req)
          })
      })
    }
    return req
  })

  axiosIns.interceptors.response.use(res => {
    var contentType = res.headers['Content-Type'] || res.headers['content-type']
    // appLog(1, `api response success, data=${JSON.stringify(res, null, 2)}`)
    if (
      isString(contentType) &&
      isString(res.data) &&
      contentType.indexOf(ENCRYTEDCONTENTTYPE2) >= 0
    ) {
      const cipher = store.rndkey
      try {
        res.data = aesDecrypt(res.data, cipher)
      } catch (e) {
        return Promise.reject({
          msg: 'decode response failed, wrong cipher'
        })
      }
    }
    return res
  })
  axiosIns.interceptors.response.use(res => {
    if (res.data && isString(res.data)) { res.data = JSON.parse(res.data) }
    // need refer to the data provided by the server
    var data = res.data
    if (data.code === 0) {
      return data.data
    }
    // session失效
    if (sessionCode.indexOf(data.code) !== -1) {
      // 清除cookie
      clearLoignCookie()

      if (Utils.isWebLogin) {
        window.location.href = Utils.getUrl('login', {
          lang: Utils.getQueryString('lang') || 'zh-HK'
        })
      } else {
        // 通知native
        handleServerError(data.code, data.data)
      }
    }

    // trade session 失效
    if (tradeSessionCode.indexOf(data.code) !== -1) {
      if (Utils.isWebLogin) {
        window.location.href = Utils.getUrl('login', {
          lang: Utils.getQueryString('lang') || 'zh-HK'
        })
      } else {
        handleTradeSessionFailed()
        return Promise.reject({ code: data.code, msg: '' })
      }
    }

    // may handle some common error
    return Promise.reject(data)
  })
  const request = async (url, options = {}) => {
    // 获取native测试环境加解密参数
    const { nativeEncrypt, nativeXsign } = (options.needEncrypt || options.needXsign)
      ? await getDebugInfo() : { nativeEncrypt: false, nativeXsign: false }
    options.needEncrypt = options.needEncrypt && nativeEncrypt
    options.needXsign = options.needXsign && nativeXsign

    const promise = new Promise((resolve, reject) => {
      options.headers = options.headers || {}
      options.url = url
      options.method = options.method || 'get'
      options.timeout = options.timeout || 90000
      options.headers['content-type'] = options['content-type'] || ''
      // need encrypt
      if (options.needEncrypt) {
        options.headers['content-type'] = encrytedContentType
      }
      options.headers['accept-language'] =
        Utils.getQueryString('lang') || 'zh-HK'
      // Add axios headers to the request
      if (
        options.method.toUpperCase() === 'GET' &&
        options.headers['content-type']
      ) {
        options.data = {
          unused: 0
        }
      }
      if (
        options.method.toUpperCase() === 'POST' &&
        !options.headers['content-type']
      ) {
        options.headers['content-type'] = 'application/json;charset=UTF-8'
      }
      // 设置通用请求头配置
      setComReqHeader(options, url, config)
      // need x-sign
      options.headers['need-xsign'] = options.needXsign ? 'x-sign' : ''

      const send = () => {
        appLog(1, `api config, config=${JSON.stringify(config, null, 2)}`)
        axiosIns.request(options)
          .then(data => {
            appLog(1, `api response success, data=${JSON.stringify(data, null, 2)}`)
            resolve(data)
          })
          .catch(err => {
            appLog(1, `api response failed, err=${JSON.stringify(err)}`)
            reject(err)
          })
      }
      send()
    })
    return promise
  }
  request.interceptors = axiosIns.interceptors
  return request
}
